import _ from 'lodash';
import React, {FC} from 'react';
import {connect} from 'react-redux';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {compareName} from '../../../../srcCommon/helpers/localeHelpers';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../srcCommon/helpers/reselectHelpers';
import {ResolveDispatchProps} from '../../../helpers/reduxHelpers';
import {CardModel, CardTypesEnum} from '../../../models/api/cardModel';
import {ConversationModel} from '../../../models/api/conversationModel';
import {VisualContactModel} from '../../../models/api/visualContactModel';
import {AppState} from '../../../store/appState';
import {ThunkAction} from '../../../store/appThunk';
import {makeGetAccount} from '../../../store/data/accounts/accountsSelectors';
import {getFlatChannels} from '../../../store/data/channels/channelsSelectors';
import {getCompanyHost} from '../../../store/data/company/companySelectors';
import {toggleContactMergeInfoBoxAction} from '../../../store/data/localPreferences/localPreferencesActions';
import {getIsContactMergeInfoBoxExpanded} from '../../../store/data/localPreferences/localPreferencesSelectors';
import {
  contactDetailsOpenAccountDetailsAction,
  contactDetailsOpenContactDetailsAction,
  createCardAndUpdateContactDetailsPanelAction,
  deleteCardAndCloseContactDetailsAsyncAction,
  popoutContactDetailsAction,
} from '../../../store/panels/contacts/contactDetailsPanelActions';
import {makeGetContactDetailsPanelSubState} from '../../../store/panels/contacts/contactDetailsPanelSelectors';
import {ContactDetailsPanelLocationsEnum} from '../../../store/panels/contacts/contactDetailsPanelState';
import {ContactDetailsPanelId} from '../../../store/panels/panelStateBase';
import {useContactDetailsContext} from '../contactDetailsContext';
import {ContactPluginHeader} from './contactPluginHeader';

/*
 * Props.
 */

interface ContactPluginHeaderContainerProps {
  panelId: ContactDetailsPanelId;
  contacts: ReadonlyArray<VisualContactModel>;
  selectedVisualContact: VisualContactModel | undefined;
  selectedCard: CardModel | undefined;
  conversation?: ConversationModel;
  isLoading?: boolean;
  location: ContactDetailsPanelLocationsEnum;
  onClose?: Handler;
  onDelete?: HandlerOf<CardModel>;
}
interface ContactPluginHeaderContainerComponentProps
  extends ContactPluginHeaderContainerProps,
    ResolveStateProps<typeof mapStateToProps>,
    ResolveDispatchProps<typeof mapDispatchToProps> {}

/*
 * Selectors.
 */

function getContacts(_state: AppState, props: ContactPluginHeaderContainerProps) {
  return props.contacts;
}

function getSelectedContactUid(_state: AppState, props: ContactPluginHeaderContainerProps) {
  return props.selectedVisualContact?.contact.uid;
}

function getAccountId(_state: AppState, props: ContactPluginHeaderContainerProps) {
  return props.selectedCard?.accountId;
}

function makeGetSortedContacts() {
  return createSelector(
    [getContacts, getFlatChannels, getSelectedContactUid],
    (contacts, channels, selectedContactUid) => {
      const channelAddressesSet = new Set(channels.map((channel) => channel.sendAddress));
      const [team, external] = _(contacts)
        .partition((contact) => channelAddressesSet.has(contact.contact.handle) || Boolean(contact.teammate))
        .value();

      return {
        external: sortContacts(external, selectedContactUid),
        team: sortContacts(team, selectedContactUid),
      };
    },
  );
}

/*
 * Component.
 */

const ContactPluginHeaderContainerComponent: FC<ContactPluginHeaderContainerComponentProps> = (props) => {
  const {
    selectedVisualContact,
    sortedPartitionedContacts,
    selectedCard,
    isLoading,
    subState,
    account,
    host,
    location,
    isMergeInfoBannerVisible,
    onDeleteCardAndCloseContactDetails,
    createCardAndUpdateContactDetailsPanel,
    openContactDetailsPopUp,
    onClose,
    onDelete,
    selectContactTab,
    selectAccountTab,
    onToggleMergeInfoBanner,
  } = props;
  const {conversationNamespace, panelId} = useContactDetailsContext();

  const createNewCardAsync = async () => {
    // If we do not have a conversation namespace, do not create a card.
    if (!conversationNamespace || !selectedVisualContact) {
      return undefined;
    }
    const card = await createCardAndUpdateContactDetailsPanel(
      panelId,
      {
        namespace: conversationNamespace,
        name: selectedVisualContact.name || selectedVisualContact.contact.handle,
        contacts: [selectedVisualContact.contact],
        type: CardTypesEnum.AUTO,
      },
      {shouldHideLoadingState: true},
    );
    return card;
  };

  return (
    <ContactPluginHeader
      selectedVisualContact={selectedVisualContact}
      sortedPartitionedContacts={sortedPartitionedContacts}
      selectedCard={selectedCard}
      isLoading={Boolean(isLoading)}
      subState={subState}
      account={account}
      host={host}
      isMergeInfoBannerVisible={isMergeInfoBannerVisible}
      shouldShowDeleteAction={location === ContactDetailsPanelLocationsEnum.CONTACTS_MANAGER}
      onDeleteCardAndCloseContactDetails={onDeleteCardAndCloseContactDetails}
      createNewCardAsync={createNewCardAsync}
      openContactDetailsPopUp={openContactDetailsPopUp}
      onClose={onClose}
      onDelete={onDelete}
      selectContactTab={selectContactTab}
      selectAccountTab={selectAccountTab}
      onToggleMergeInfoBanner={onToggleMergeInfoBanner}
    />
  );
};

/*
 * Helpers.
 */

/** Sorts the passed in contacts alphabetically with the selected contact first */
function sortContacts(contacts: VisualContactModel[], selectedContactUid?: string) {
  return [...contacts].sort((c1, c2) => {
    if (c2.contact.uid === selectedContactUid) {
      return 1;
    }
    if (c1.contact.uid === selectedContactUid) {
      return -1;
    }
    return compareName(c1.name || c1.contact.handle, c2.name || c2.contact.handle);
  });
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ContactPluginHeaderContainerProps>()(() => ({
  sortedPartitionedContacts: makeGetSortedContacts(),
  subState: makeGetContactDetailsPanelSubState(),
  account: makeGetAccount(getAccountId),
  host: getCompanyHost,
  isMergeInfoBannerVisible: getIsContactMergeInfoBoxExpanded,
}));

const mapDispatchToProps = makeMapDispatchToProps<ContactPluginHeaderContainerProps>()(
  ({panelId, selectedCard, selectedVisualContact, location, conversation}) => ({
    createCardAndUpdateContactDetailsPanel: createCardAndUpdateContactDetailsPanelAction,
    onDeleteCardAndCloseContactDetails: deleteCardAndCloseContactDetailsAsyncAction,
    openContactDetailsPopUp:
      (popupWidth: number): ThunkAction =>
      (dispatch) => {
        // If we do not have a selected contact, we can't pop it out.
        if (!selectedVisualContact) {
          return;
        }
        dispatch(
          popoutContactDetailsAction({
            parentPanelId: panelId,
            selectedContactUid: selectedVisualContact.contact.uid,
            popupWidth,
            popupConversationId: conversation?.id,
            selectedCardId: selectedCard?.id,
            shouldHideHeaderTabs: location === ContactDetailsPanelLocationsEnum.CONTACTS_MANAGER,
          }),
        );
      },
    onToggleMergeInfoBanner: toggleContactMergeInfoBoxAction,
    selectContactTab: () => contactDetailsOpenContactDetailsAction(panelId),
    selectAccountTab: () => contactDetailsOpenAccountDetailsAction(panelId, selectedCard?.accountId),
  }),
);

export const ContactPluginHeaderContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ContactPluginHeaderContainerComponent);
